import { Component, inject, ChangeDetectionStrategy, signal, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { OauthService } from '../../services/oauth.service';
import { Router } from '@angular/router';
import { logos } from '../common/properties';
import { GlobalTexts } from '../common/globalText';
import { AppSnackService } from '../../services/app-snack.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { DomSanitizer } from '@angular/platform-browser';
import { finalize } from 'rxjs';
import { RecaptchaService } from '../../services/recaptcha.service';
import { MatDialog } from '@angular/material/dialog';
import { ChangelogDlgComponent, SystemInfoDialogData } from '../home/changelog-dlg/changelog-dlg.component';

import { Creds } from '../../data/schemas/user.schema';
/**
 * Interface para personalizar las etiquetas del componente de login.
 */
export interface LoginLabels {
  user: string;
  password: string;
  userIsRequired: string;
  notAValidUser: string;
  passwordIsrequired: string;
  recoverPassword: string;
  loginButton: string;
  waitDuo?: string;
  systemNotice?: string;
}

@Component({
  selector: 'app-app-login',
  imports: [
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    FormsModule,
    ReactiveFormsModule,
    MatProgressSpinner,
  ],
  templateUrl: './app-login.component.html',
  styleUrl: './app-login.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppLoginComponent implements OnInit {
  private readonly oauthSvc = inject(OauthService);
  private readonly router = inject(Router);
  private readonly snackBar = inject(AppSnackService);
  private readonly recaptchaService = inject(RecaptchaService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly iconRegistry = inject(MatIconRegistry);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly dialog = inject(MatDialog);

  protected readonly gTexts = inject(GlobalTexts);

  public readonly logoUrl = logos.app;
  public readonly brandLogoUrl = logos.agd;
  public readonly duoLogoUrl = logos.duo;
  /** Nombre de lo aplicación dividido en partes para el layout responsivo. */
  public appNameParts: string[] = [];
  /** Ancho de pantalla actual para control de layout (opcional, usa window.innerWidth por defecto). */
  public screenWidth = window.innerWidth;
  public readonly labels = this.gTexts.loginLabels;

  public readonly loading = signal(false);
  public readonly duoWait = signal(false);
  public readonly recaptchaReady = signal(false);
  public readonly viewReset = signal(false);
  public readonly hide = signal(true);

  public readonly formLogin = this.formBuilder.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(4), Validators.maxLength(50)]],
  });

  public readonly resetLogin = this.formBuilder.group({
    email: ['', [Validators.required, Validators.email]],
  });

  ngOnInit(): void {
    this.iconRegistry.addSvgIcon(
      'whatsapp',
      this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/whatsapp.svg'),
    );
    this.recaptchaService.execute('init').subscribe({
      next: () => this.recaptchaReady.set(true),
      error: () => this.recaptchaReady.set(true),
    });
  }

  clickEvent(event: MouseEvent) {
    this.hide.set(!this.hide());
    event.stopPropagation();
  }

  login() {
    if (this.formLogin.invalid) return;

    this.duoWait.set(true);
    try {
      this.recaptchaService.execute('login').subscribe({
        next: (recaptchaToken) => {
          this.oauthSvc
            .login(this.formLogin.value as Creds, recaptchaToken!)
            .pipe(finalize(() => this.duoWait.set(false)))
            .subscribe({
              next: (response) => {
                this.snackBar.openWithContext(
                  `Bienvenido ${response.user?.['firstName'] ?? ''} ${response.user?.['lastName'] ?? ''}`,
                  'success',
                );
                this.router.navigate(['/home']);
              },
              error: (e) =>{
                console.error(e);
                this.duoWait.set(false);
                this.snackBar.openWithContext(`Error de credenciales: ${e.error.error}`, 'error');
              }
            });
        },
        error: (err) => {
          console.error('Error executing reCAPTCHA:', err);
          this.snackBar.openWithContext('Error con Google reCAPTCHA', 'error');
          this.duoWait.set(false);
        },
      });
    } catch (error) {
      console.error('Error inesperado en login:', error);
      this.duoWait.set(false);
    }
  }

  toggleHelp(reset: boolean) {
    this.viewReset.set(reset);
  }

  /** Abre el estado de los servicios GNexus consultando el endpoint que no requiere sesión. */
  verEstadoServicios() {
    this.dialog.open(ChangelogDlgComponent, {
      width: '600px',
      maxHeight: '80vh',
      data: { initialTab: 'servicios', fromLogin: true } satisfies SystemInfoDialogData,
    });
  }

  reset() {
    const email = this.resetLogin.get('email')?.value;
    if (!email) return;

    this.loading.set(true);
    this.oauthSvc
      .forgotPassword(email)
      .pipe(
        finalize(() => {
          this.loading.set(false);
          this.viewReset.set(false);
        }),
      )
      .subscribe({
        next: () => this.snackBar.openWithContext('Contraseña reestablecida correctamente', 'success'),
        error: (err) => console.error('Error al resetear password:', err),
      });
  }
}
